/**
 * Mobile bottom tab bar: tab keys, labels, destinations and active-tab rules.
 * Contract: plans/mobile-v2.md §3.2. The user's 4 tabs come from the
 * `preferences.mobileTabs` shared prop (see lib/stores/mobileTabs.ts).
 */
import { pagePathname } from './pageHotkeys';

export const MOBILE_TAB_KEYS = [
  'timeline',
  'projects',
  'now',
  'habits',
  'calendar',
  'cycles',
  'boards',
] as const;

export type MobileTabKey = (typeof MOBILE_TAB_KEYS)[number];

/** A configurable tab, or the fixed More tab. */
export type MobileTabId = MobileTabKey | 'more';

export const MOBILE_TAB_COUNT = 4;

export const DEFAULT_MOBILE_TABS: MobileTabKey[] = ['timeline', 'projects', 'now', 'habits'];

export const MOBILE_TAB_LABELS: Record<MobileTabId, string> = {
  timeline: 'Timeline',
  projects: 'Projects',
  now: 'Now',
  habits: 'Habits',
  calendar: 'Calendar',
  cycles: 'Cycles',
  boards: 'Boards',
  more: 'More',
};

/** Fixed destinations. Timeline is dynamic (see `mobileTabHref`). */
const TAB_PATHS: Record<Exclude<MobileTabKey, 'timeline'>, string> = {
  projects: '/projects',
  now: '/now/board',
  habits: '/habits',
  calendar: '/events_calendar',
  cycles: '/cycles/current',
  boards: '/boards',
};

/** URL prefixes that belong to each section. */
const SECTION_PREFIXES: Record<MobileTabKey | 'more', string[]> = {
  timeline: ['/month', '/week', '/year', '/dashboard'],
  projects: ['/projects'],
  now: ['/now'],
  habits: ['/habits'],
  calendar: ['/events_calendar'],
  cycles: ['/cycles'],
  boards: ['/boards'],
  more: ['/more', '/settings', '/trash', '/changelog'],
};

export function isMobileTabKey(value: unknown): value is MobileTabKey {
  return typeof value === 'string' && (MOBILE_TAB_KEYS as readonly string[]).includes(value);
}

/** True for exactly MOBILE_TAB_COUNT unique, known keys (mirrors the server validation). */
export function isValidMobileTabs(value: unknown): value is MobileTabKey[] {
  return (
    Array.isArray(value) &&
    value.length === MOBILE_TAB_COUNT &&
    value.every(isMobileTabKey) &&
    new Set(value).size === MOBILE_TAB_COUNT
  );
}

export function normalizeMobileTabs(value: unknown): MobileTabKey[] {
  return isValidMobileTabs(value) ? [...value] : [...DEFAULT_MOBILE_TABS];
}

export function mobileTabHref(key: MobileTabKey, timelinePath: string): string {
  return key === 'timeline' ? timelinePath : TAB_PATHS[key];
}

function matchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** The app section a URL belongs to, or null for pages outside the tab structure. */
export function mobileSectionForUrl(url: string | null | undefined): MobileTabId | null {
  const path = pagePathname(url);
  for (const [section, prefixes] of Object.entries(SECTION_PREFIXES)) {
    if (prefixes.some((prefix) => matchesPrefix(path, prefix))) return section as MobileTabId;
  }
  return null;
}

/**
 * The highlighted tab for a URL. Sections that aren't one of the user's tabs
 * (e.g. Calendar when it isn't configured) fall under More.
 */
export function activeMobileTab(
  url: string | null | undefined,
  tabs: readonly MobileTabKey[],
): MobileTabId | null {
  const section = mobileSectionForUrl(url);
  if (!section || section === 'more') return section;
  return tabs.includes(section) ? section : 'more';
}

/**
 * Keys offered on the More page's Plan list: sections that aren't tabs, in
 * catalogue order (Timeline first if the user removed it).
 */
export function moreSectionKeys(tabs: readonly MobileTabKey[]): MobileTabKey[] {
  return MOBILE_TAB_KEYS.filter((key) => !tabs.includes(key));
}

/**
 * Put `key` in slot `index`. If `key` is already in another slot the two swap,
 * so the result is always a valid, unique tab set.
 */
export function setMobileTabSlot(
  tabs: readonly MobileTabKey[],
  index: number,
  key: MobileTabKey,
): MobileTabKey[] {
  const next = [...tabs];
  const existing = next.indexOf(key);
  if (existing === index) return next;
  if (existing !== -1) next[existing] = next[index];
  next[index] = key;
  return next;
}
